import { type DragEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  Address,
  CreateLocationParams,
  IsoCountryCode,
  ISO_3166_1_ALPHA_2_CODES,
  isISO31661Alpha2Code,
  Location,
  UpdateLocationParams,
} from "@forgepoint/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImagePlus, Loader, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";

interface LocationFormData {
  name: string;
  description: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
}

const emptyFormData: LocationFormData = {
  name: "",
  description: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zipcode: "",
  country: "",
};

/**
 * Extracts form data from an existing location for editing.
 * @param location - The location to extract form data from
 * @returns The form data populated from the location
 */
const getFormDataFromLocation = (location: Location): LocationFormData => ({
  name: location.name,
  description: location.description ?? "",
  addressLine1: location.address?.address_line_1 ?? "",
  addressLine2: location.address?.address_line_2 ?? "",
  city: location.address?.city ?? "",
  state: location.address?.state ?? "",
  zipcode: location.address?.zipcode ?? "",
  country: location.address?.country ?? "",
});

/**
 * Builds an Address object from form data, returning null if no address fields are filled.
 * @param formData - The form data containing address fields
 * @returns The constructed Address or null
 */
function buildAddress(formData: LocationFormData): Address | null {
  const hasAddress = [
    formData.addressLine1,
    formData.addressLine2,
    formData.city,
    formData.state,
    formData.zipcode,
    formData.country,
  ].some((value) => value.trim());

  if (!hasAddress) {
    return null;
  }

  return {
    name: null,
    address_line_1: formData.addressLine1.trim() || null,
    address_line_2: formData.addressLine2.trim() || null,
    city: formData.city.trim() || null,
    state: formData.state.trim() || null,
    zipcode: formData.zipcode.trim() || null,
    country: formData.country.trim().toUpperCase() || null,
  };
}

type LocationEditorSheetProps =
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      mode: "create";
      location?: undefined;
      readOnly?: undefined;
      onSave: (data: CreateLocationParams) => Promise<true | string>;
    }
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      mode: "edit";
      location: Location;
      /** Renders fields non-editable and hides Save when the user lacks edit access. */
      readOnly?: boolean;
      onSave: (data: UpdateLocationParams) => Promise<true | string>;
    };

/**
 * Sheet component for creating or editing a location (facility).
 * Supports logo upload/preview/remove, name, description, and address fields.
 * Name and address are read-only on locations synced from an integration.
 * @param props - The sheet props
 * @returns The location editor sheet
 */
export function LocationEditorSheet({
  open,
  onOpenChange,
  mode,
  location,
  readOnly = false,
  onSave,
}: LocationEditorSheetProps) {
  const [formData, setFormData] = useState<LocationFormData>(emptyFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [logoAssetId, setLogoAssetId] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isSynced = Boolean(location?.externalMappings.length);
  const isSyncedFieldReadOnly = readOnly || isSynced;

  const countryOptions = useMemo(
    () =>
      ISO_3166_1_ALPHA_2_CODES.map(
        (code): { code: IsoCountryCode; label: string } => ({
          code,
          label: code,
        }),
      ),
    [],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === "edit" && location) {
      setFormData(getFormDataFromLocation(location));
      setLogoPreviewUrl(location.logoUrl ?? null);
      setLogoAssetId(null);
    } else {
      setFormData(emptyFormData);
      setLogoPreviewUrl(null);
      setLogoAssetId(null);
    }
  }, [open, mode, location]);

  /**
   * Handles logo file upload via presigned URL.
   * @param file - The image file selected by the user
   */
  const handleLogoUpload = async (file: File) => {
    const allowedImageMimeTypes = new Set([
      "image/png",
      "image/jpeg",
      "image/webp",
      "image/gif",
    ]);
    if (!allowedImageMimeTypes.has(file.type)) {
      toast.error("Please upload a PNG, JPEG, WEBP, or GIF image");
      return;
    }
    setIsUploadingLogo(true);
    try {
      const response = await fetchApi("api/assets/upload", "POST", {
        body: {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        },
      });

      if (!response.ok || !response.data) {
        throw new Error("Failed to get upload URL");
      }

      const { uploadUrl, asset } = response.data as {
        uploadUrl: string;
        asset: { id: string };
      };

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file");
      }

      setLogoAssetId(asset.id);
      setLogoPreviewUrl(URL.createObjectURL(file));
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
      );
      toast.error("Failed to upload logo");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  /** Removes the current logo. */
  const handleRemoveLogo = () => {
    setLogoAssetId(null);
    setLogoPreviewUrl(null);
  };

  const handleCountrySelect = (value: string) => {
    if (value !== "" && !isISO31661Alpha2Code(value)) {
      return;
    }
    setFormData((currentFormData) => ({
      ...currentFormData,
      country: value,
    }));
  };

  /**
   * Handles drag-over events on the logo drop zone.
   * @param e - The drag event
   */
  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  /**
   * Handles drag-leave events on the logo drop zone.
   * @param e - The drag event
   */
  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };

  /**
   * Handles file drop events on the logo drop zone.
   * @param e - The drag event containing the dropped file
   */
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleLogoUpload(file);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Location name is required");
      return;
    }

    setIsSaving(true);
    try {
      const result = await saveLocation({
        description: formData.description.trim() || null,
        ...(logoAssetId !== null || logoPreviewUrl === null
          ? { logoAssetId }
          : {}),
      });
      if (result === true) {
        toast.success(
          mode === "create" ? "Location created" : "Location updated",
        );
        onOpenChange(false);
      } else {
        toast.error(result);
      }
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Saves the location, omitting the integration-owned name and address on synced locations.
   * @param editableFields - The fields that stay editable on synced locations
   * @returns True on success, or an error message
   */
  const saveLocation = (
    editableFields: Pick<UpdateLocationParams, "description" | "logoAssetId">,
  ): Promise<true | string> => {
    if (mode === "edit" && isSynced) {
      return onSave(editableFields);
    }
    return onSave({
      ...editableFields,
      name: formData.name.trim(),
      address: buildAddress(formData),
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[640px] overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <SheetTitle>
              {mode === "create" ? "New Location" : "Edit Location"}
            </SheetTitle>
            {isSynced && (
              <Badge
                variant="outline"
                className="text-[10px] font-normal tracking-wider uppercase px-1.5 py-0 border-muted-foreground/25"
              >
                Synced
              </Badge>
            )}
          </div>
          <SheetDescription>
            {isSynced
              ? "This location is managed by an integration. Name and address are read-only in Forgepoint."
              : "Internal operating facility used for forecasting."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Logo upload zone */}
          <div className="space-y-2">
            <Label>Logo</Label>
            {logoPreviewUrl ? (
              <div
                className={`
                  relative w-full rounded-md border-2 border-dashed py-6
                  flex items-center justify-center
                  transition-colors group
                  ${isDraggingOver ? "border-primary/50 bg-primary/5" : "border-border hover:border-muted-foreground/30 hover:bg-muted/50"}
                  ${isUploadingLogo ? "pointer-events-none opacity-60" : "cursor-pointer"}
                `}
                onClick={() => !readOnly && fileInputRef.current?.click()}
                onDragOver={readOnly ? undefined : handleDragOver}
                onDragLeave={readOnly ? undefined : handleDragLeave}
                onDrop={readOnly ? undefined : handleDrop}
              >
                <img
                  src={logoPreviewUrl}
                  alt="Location logo"
                  className="max-h-24 max-w-full object-contain"
                />
                {!readOnly && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                    {isUploadingLogo ? (
                      <Loader2 className="h-5 w-5 animate-spin text-white" />
                    ) : (
                      <span className="text-xs text-white font-medium">
                        Click or drag to replace
                      </span>
                    )}
                  </div>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveLogo();
                    }}
                    disabled={isUploadingLogo}
                    className="absolute top-1.5 right-1.5 rounded-full bg-destructive p-1 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/90"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => !readOnly && fileInputRef.current?.click()}
                onDragOver={readOnly ? undefined : handleDragOver}
                onDragLeave={readOnly ? undefined : handleDragLeave}
                onDrop={readOnly ? undefined : handleDrop}
                disabled={isUploadingLogo || readOnly}
                className={`
                  w-full rounded-md border-2 border-dashed py-6
                  flex flex-col items-center gap-2
                  text-muted-foreground
                  transition-colors
                  ${isDraggingOver ? "border-primary/50 bg-primary/5" : "border-border hover:border-muted-foreground/30 hover:bg-muted/50"}
                  ${isUploadingLogo ? "pointer-events-none opacity-60" : "cursor-pointer"}
                `}
              >
                {isUploadingLogo ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <ImagePlus className="h-5 w-5" />
                )}
                <span className="text-xs">
                  {isUploadingLogo
                    ? "Uploading..."
                    : readOnly
                      ? "No logo"
                      : "Click or drag an image to upload"}
                </span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleLogoUpload(file);
                  e.target.value = "";
                }
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location-name">Name</Label>
            <Input
              id="location-name"
              value={formData.name}
              onChange={(event) =>
                setFormData({ ...formData, name: event.target.value })
              }
              placeholder="Southaven"
              disabled={isSyncedFieldReadOnly}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location-description">Description</Label>
            <Textarea
              id="location-description"
              value={formData.description}
              onChange={(event) =>
                setFormData({ ...formData, description: event.target.value })
              }
              placeholder="Internal notes"
              rows={3}
              disabled={readOnly}
            />
          </div>

          {/* Address section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Address
              </span>
              <Separator className="flex-1" />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="location-address-1">Address line 1</Label>
                <Input
                  id="location-address-1"
                  value={formData.addressLine1}
                  onChange={(event) =>
                    setFormData({
                      ...formData,
                      addressLine1: event.target.value,
                    })
                  }
                  disabled={isSyncedFieldReadOnly}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="location-address-2">Address line 2</Label>
                <Input
                  id="location-address-2"
                  value={formData.addressLine2}
                  onChange={(event) =>
                    setFormData({
                      ...formData,
                      addressLine2: event.target.value,
                    })
                  }
                  disabled={isSyncedFieldReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location-city">City</Label>
                <Input
                  id="location-city"
                  value={formData.city}
                  onChange={(event) =>
                    setFormData({ ...formData, city: event.target.value })
                  }
                  disabled={isSyncedFieldReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location-state">
                  State / Province / Region
                </Label>
                <Input
                  id="location-state"
                  value={formData.state}
                  onChange={(event) =>
                    setFormData({ ...formData, state: event.target.value })
                  }
                  disabled={isSyncedFieldReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location-zip">ZIP / Postal code</Label>
                <Input
                  id="location-zip"
                  value={formData.zipcode}
                  onChange={(event) =>
                    setFormData({ ...formData, zipcode: event.target.value })
                  }
                  disabled={isSyncedFieldReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location-country">Country</Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={formData.country}
                    onValueChange={handleCountrySelect}
                    disabled={isSyncedFieldReadOnly}
                  >
                    <SelectTrigger id="location-country" className="flex-1">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countryOptions.map(({ code, label }) => (
                        <SelectItem key={code} value={code}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.country && !isSyncedFieldReadOnly ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Clear country"
                      title="Clear country"
                      onClick={() => handleCountrySelect("")}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        <SheetFooter className="mt-8">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button
              type="button"
              onClick={handleSave}
              disabled={isSaving || isUploadingLogo}
            >
              {isSaving && <Loader className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
